import { FieldKind, type FieldValue } from './field-kind';
import { PLACEHOLDER_STYLE, type FieldBinding } from './field-reader';
import { W14_NS, W_NS, XML_NS, attr, descendants, firstKid, kids } from './ns';
import { CHECKED_OFF, CHECKED_ON, readGlyphs } from './checkbox-glyph';
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_LOCALE, formatDate, toIsoDate } from './date-format';

export function writeValue(binding: FieldBinding, value: FieldValue): void {
  if (binding.field.kind === FieldKind.CheckBox) {
    writeCheckBox(binding, value === true);

    return;
  }

  const text = String(value);

  clearPlaceholder(binding.sdt);

  if (binding.field.kind === FieldKind.Date) {
    writeDate(binding, text);

    return;
  }

  writeText(binding, text);
}

// A date (yyyy-mm-dd) goes into w:fullDate and appears in the picker's own format,
// the way Word shows it. Anything else, such as an empty value or "Onbekend", is
// written as plain text and leaves the picker without a date.
function writeDate(binding: FieldBinding, value: string): void {
  const props = firstKid(binding.sdt, 'sdtPr');
  const date = props ? firstKid(props, 'date') : undefined;
  const iso = toIsoDate(value);

  if (!date || !iso) {
    date?.removeAttributeNS(W_NS, 'fullDate');
    writeText(binding, value);

    return;
  }

  date.setAttributeNS(W_NS, 'w:fullDate', `${iso}T00:00:00Z`);

  const format = attr(firstKid(date, 'dateFormat'), 'val') ?? DEFAULT_DATE_FORMAT;
  const locale = attr(firstKid(date, 'lid'), 'val') ?? DEFAULT_DATE_LOCALE;

  writeText(binding, formatDate(iso, format, locale));
}

function clearPlaceholder(sdt: Element): void {
  const props = firstKid(sdt, 'sdtPr');
  const flag = props ? firstKid(props, 'showingPlcHdr') : undefined;

  flag?.remove();
}

function writeText(binding: FieldBinding, text: string): void {
  const { content } = binding;
  const doc = content.ownerDocument;

  const runProps = templateRunProps(content);
  const paraProps = firstParaProps(content);
  const lines = text.split('\n');

  clear(content);

  if (!binding.field.multiline) {
    content.appendChild(makeRun(doc, lines.join(' '), runProps));

    return;
  }

  for (const line of lines) {
    const p = doc.createElementNS(W_NS, 'w:p');

    if (paraProps) {
      p.appendChild(paraProps.cloneNode(true));
    }

    p.appendChild(makeRun(doc, line, runProps));
    content.appendChild(p);
  }
}

function writeCheckBox(binding: FieldBinding, checked: boolean): void {
  const props = firstKid(binding.sdt, 'sdtPr');
  const checkbox = props ? firstKid(props, 'checkbox', W14_NS) : undefined;
  if (!checkbox) {
    return;
  }

  const flag = firstKid(checkbox, 'checked', W14_NS) ?? addChecked(checkbox);
  flag.setAttributeNS(W14_NS, 'w14:val', checked ? CHECKED_ON : CHECKED_OFF);

  const glyphs = readGlyphs(checkbox);
  const target = descendants(binding.content, 't')[0];

  if (target) {
    target.textContent = checked ? glyphs.checked : glyphs.unchecked;

    return;
  }

  const doc = binding.content.ownerDocument;
  binding.content.appendChild(makeRun(doc, checked ? glyphs.checked : glyphs.unchecked, undefined));
}

function addChecked(checkbox: Element): Element {
  const flag = checkbox.ownerDocument.createElementNS(W14_NS, 'w14:checked');
  checkbox.insertBefore(flag, checkbox.firstChild);

  return flag;
}

function templateRunProps(content: Element): Element | undefined {
  const run = descendants(content, 'r')[0];
  const props = run ? firstKid(run, 'rPr') : undefined;
  if (!props) {
    return undefined;
  }

  const copy = props.cloneNode(true) as Element;
  const style = firstKid(copy, 'rStyle');

  if (attr(style, 'val') === PLACEHOLDER_STYLE) {
    style?.remove();
  }

  return copy.children.length > 0 ? copy : undefined;
}

function firstParaProps(content: Element): Element | undefined {
  const paragraph = kids(content, 'p')[0];

  return paragraph ? firstKid(paragraph, 'pPr') : undefined;
}

function makeRun(doc: Document, text: string, runProps: Element | undefined): Element {
  const run = doc.createElementNS(W_NS, 'w:r');

  if (runProps) {
    run.appendChild(runProps.cloneNode(true));
  }

  const t = doc.createElementNS(W_NS, 'w:t');
  t.setAttributeNS(XML_NS, 'xml:space', 'preserve');
  t.textContent = text;
  run.appendChild(t);

  return run;
}

function clear(el: Element): void {
  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }
}
